import { formatPrice, calculateSavings } from "./mockWinnerData";

const PLACEHOLDER_HOST_PATTERN = /example\.(com|org|net)/i;

const escapeSvgText = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const clampLabel = (value: string, maxLength = 28) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Aukce";
  }

  return trimmed.length > maxLength
    ? `${trimmed.slice(0, maxLength - 1)}...`
    : trimmed;
};

const coercePositiveNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value
    .trim()
    .replace(/\s+/g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const createAuctionPlaceholderImage = (
  title: string,
  subtitle = "trefim.to"
): string => {
  const safeTitle = escapeSvgText(clampLabel(title));
  const safeSubtitle = escapeSvgText(clampLabel(subtitle, 20));
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="675" viewBox="0 0 1200 675">
      <rect width="1200" height="675" fill="#0f1022"/>
      <rect x="52" y="52" width="1096" height="571" rx="36" fill="#171a38"/>
      <circle cx="156" cy="170" r="42" fill="#f5a623"/>
      <text x="228" y="184" fill="#f5a623" font-family="Arial, sans-serif" font-size="54" font-weight="700">${safeSubtitle}</text>
      <text x="96" y="324" fill="#ffffff" font-family="Arial, sans-serif" font-size="72" font-weight="700">${safeTitle}</text>
      <text x="96" y="398" fill="#9fa8c7" font-family="Arial, sans-serif" font-size="30">Obrázek produktu se načítá nebo není k dispozici.</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

export const isUsableImageUrl = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return !PLACEHOLDER_HOST_PATTERN.test(trimmed);
};

export const sanitizeAuctionPhotoUrls = (
  photoUrls: unknown,
  title: string
): string[] => {
  if (!Array.isArray(photoUrls)) {
    return [createAuctionPlaceholderImage(title)];
  }

  const usableUrls = photoUrls.filter(isUsableImageUrl);
  return usableUrls.length > 0
    ? usableUrls
    : [createAuctionPlaceholderImage(title)];
};

export const resolveAuctionPrice = (
  payload: Record<string, unknown>
): number | undefined => {
  const priceFields = [
    "price",
    "currentPrice",
    "currentBid",
    "startingPrice",
    "startPrice",
    "auctionPrice",
  ];

  for (const field of priceFields) {
    const resolved = coercePositiveNumber(payload[field]);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

export const resolveEstimatedRetailPrice = (
  payload: Record<string, unknown>
): number | undefined => {
  const retailFields = [
    "estimatedRetailPrice",
    "retailPrice",
    "originalPrice",
    "regularPrice",
    "listPrice",
  ];

  for (const field of retailFields) {
    const resolved = coercePositiveNumber(payload[field]);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

export const normalizeAuctionStatus = (
  status: unknown
): "active" | "completed" | "pending" => {
  if (status === "completed" || status === "pending" || status === "active") {
    return status;
  }

  return "active";
};

export const getAuctionPriceDisplay = (price?: number) => {
  if (!price || price <= 0) {
    return {
      value: "Cena se připravuje",
      suffix: undefined,
      full: "Cena se připravuje",
    };
  }

  const full = formatPrice(price);
  const value = full.replace(/\s*Kč$/, "");

  return {
    value,
    suffix: "Kč",
    full,
  };
};

export const getAuctionSavingsLabel = (
  price?: number,
  estimatedRetailPrice?: number
): string | null => {
  if (!price || !estimatedRetailPrice || estimatedRetailPrice <= price) {
    return null;
  }

  return calculateSavings(price, estimatedRetailPrice);
};

export const getAuctionStatusLabel = (
  status?: "active" | "completed" | "pending"
): string => {
  if (status === "completed") {
    return "Ukončena";
  }

  if (status === "pending") {
    return "Připravujeme";
  }

  return "Probíhá";
};
